import React, { useState } from 'react'
import { AdCreative, AssetApprovalStatus } from '@marketing-milk/interfaces'
import { CreativeMedia } from '../../creatives/CreativeCard/CreativeMedia'
import { StatusPill } from './StatusPill'
import { dark, radius, status as statusColors, type as typo } from '../tokens'

type Props = {
  creative: AdCreative
  /** Open the full-size preview on this ad (view mode). */
  onOpen: () => void
  /** Open the full-size preview on this ad with the reject-reason form already showing. */
  onOpenReject: () => void
  /**
   * Record an approval. Supplied by the page, which owns the one mutation for the
   * whole grid — the card must not open its own, or the payload it sends drifts
   * from the modal's.
   */
  onApprove: () => void
  submitting: boolean
}

/**
 * One ad in the client review grid: thumbnail, current status, and quick
 * Approve/Reject actions. Clicking the card (or its Reject button) opens the
 * full-size preview with the ad's copy/metadata alongside the same actions —
 * see `ReviewCreativePreviewModal`, rendered once by the parent page (not
 * here) so a successful decision there can advance straight into the next
 * card's ad instead of just closing. The quick Approve button below is a
 * separate, immediate action that never opens anything.
 *
 * A published ad is live on Facebook, so neither verdict means anything and the
 * server refuses both — the actions are hidden rather than left to 400.
 */
export function ReviewCreativeCard({
  creative,
  onOpen,
  onOpenReject,
  onApprove,
  submitting,
}: Props) {
  const [hovered, setHovered] = useState(false)

  const latestExternalReview = [...creative.reviews]
    .filter(review => review.type === 'external')
    .sort((a, b) => b.createdAt - a.createdAt)[0]

  const canReview = creative.reviewStatus !== AssetApprovalStatus.Published

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        // No shadow: on the near-black ground a drop shadow is invisible, so
        // elevation comes from the surface lift, and hover moves the border to
        // the accent instead.
        border: `1px solid ${hovered ? dark.accent : dark.hairline}`,
        borderRadius: radius.lg,
        overflow: 'hidden',
        background: dark.surface,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-3px)' : 'none',
        transition: 'border-color 0.15s ease, transform 0.15s ease',
      }}
    >
      <div style={{ position: 'relative', background: dark.bg }}>
        <CreativeMedia creative={creative} className="h-48 w-full" />
        <div style={{ position: 'absolute', top: 10, left: 10 }}>
          <StatusPill creative={creative} />
        </div>
        {hovered && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.25)',
              color: '#fff',
              fontFamily: typo.family.sans,
              fontSize: typo.size.eyebrow,
              fontWeight: typo.weight.semibold,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            View ad
          </div>
        )}
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          style={{
            fontFamily: typo.family.sans,
            fontWeight: typo.weight.semibold,
            fontSize: typo.size.bodyLg,
            color: dark.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {creative.name}
        </div>

        {latestExternalReview && !latestExternalReview.approved && latestExternalReview.notes && (
          <div
            style={{
              fontFamily: typo.family.sans,
              fontSize: typo.size.body,
              color: dark.textMuted,
              fontStyle: 'italic',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            “{latestExternalReview.notes}”
          </div>
        )}

        {canReview && (
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              disabled={submitting}
              onClick={e => {
                e.stopPropagation()
                onApprove()
              }}
              style={approveButtonStyle(submitting)}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={e => {
                e.stopPropagation()
                onOpenReject()
              }}
              style={rejectButtonStyle(submitting)}
            >
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const baseButtonStyle: React.CSSProperties = {
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: radius.sm,
  padding: '8px 12px',
  fontFamily: typo.family.sans,
  fontSize: typo.size.body,
  fontWeight: typo.weight.semibold,
  cursor: 'pointer',
}

const approveButtonStyle = (disabled: boolean): React.CSSProperties => ({
  ...baseButtonStyle,
  background: statusColors.success,
  border: `1px solid ${statusColors.success}`,
  // Dark ink on the bright success fill: white over #22c55e is barely 2.3:1.
  color: dark.accentInk,
  opacity: disabled ? 0.6 : 1,
  cursor: disabled ? 'default' : 'pointer',
})

const rejectButtonStyle = (disabled: boolean): React.CSSProperties => ({
  ...baseButtonStyle,
  background: 'transparent',
  border: `1px solid ${statusColors.danger}`,
  color: statusColors.danger,
  opacity: disabled ? 0.6 : 1,
  cursor: disabled ? 'default' : 'pointer',
})
